import React from 'react';
import { Sales } from '../../types';
import { toPercent } from '../../../../../../utils/math';
import { FormattedNumber } from '../../../../../../components';
import styles from './Analytics.module.css';
import { AmountsList, AmountsListItem } from './AmountsList';
import { LegendItem } from './LegendItem';
import { FORMAT, NUMBER_STYLE } from '../../constants';
import { ProgressBar } from './ProgressBar';
import { Limit } from './Limit';
import { useTranslation } from '../../../../../../hooks';
import { Card } from '@mui/material';

interface Props {
  todaySales: Sales;
}

export const DefaultView = ({ todaySales }: Props) => {
  const { pdz, fact, free, limit } = todaySales;
  const pdzPercent = toPercent(pdz, fact);
  const freePercent = toPercent(free, limit);

  const amounts: AmountsListItem[] = [
    { key: 'fact', value: fact, dotClass: styles.amountsDotFact },
    { key: 'pdz', value: pdz, dotClass: styles.amountsDotPdz },
    { key: 'free', value: free, dotClass: styles.amountsDotFree },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: 'common.white',
        m: '10px',
        p: '10px',
        boxShadow: '0 0 22px 2px rgba(0, 0, 0, 0.1)',
        borderRadius: '15px',
      }}
    >
      <div className={styles.amounts}>
        <div className={styles.progressLegend}>
          <LegendItem
            width={`${pdzPercent}%`}
            value={pdz || undefined}
            options={FORMAT.small}
            style={NUMBER_STYLE.small}
          />
          <LegendItem
            width="100%"
            value={fact || undefined}
            options={FORMAT.small}
            style={NUMBER_STYLE.small}
          />
          <LegendItem
            width={`${freePercent}%`}
            value={free && free > 0 ? free : undefined}
            options={FORMAT.small}
            style={NUMBER_STYLE.small}
          />
        </div>

        <ProgressBar
          pdzPercent={pdzPercent}
          freePercent={freePercent}
          showPdz
        />

        {limit !== undefined && <Limit limit={limit} />}

        <AmountsList items={amounts} style={{ paddingTop: 5 }} />
      </div>
    </Card>
  );
};

export const PilotView = ({ todaySales }: Props) => {
  const { t } = useTranslation();
  const { fact, free, limit, pdzDays, overdue, penaltyPct, pdz } = todaySales;
  const freePercent = toPercent(free, limit);
  const pdzPercent = toPercent(pdz, fact);

  const amounts: AmountsListItem[] = [
    { key: 'fact', value: fact, dotClass: styles.amountsDotFact },
    { key: 'free', value: free, dotClass: styles.amountsDotFree },
  ];

  return (
    <div className={styles.main}>
      <Card
        className={styles.card}
        elevation={0}
        sx={{ backgroundColor: 'common.white' }}
      >
        <div className={styles.debtCard}>
          {pdzDays !== undefined && (
            <div className={styles.debtField}>
              <div className={styles.debtText} style={{ fontSize: '0.65rem' }}>
                <div>{t('pdzDay')}</div>
                <FormattedNumber
                  value={pdzDays}
                  options={FORMAT.compact}
                  style={NUMBER_STYLE.big}
                />
              </div>
            </div>
          )}

          {overdue !== undefined && (
            <div className={styles.debtField}>
              <div className={styles.debtText} style={{ fontSize: '0.65rem' }}>
                <div>⠀⠀</div>
                {overdue > 0 && <span style={NUMBER_STYLE.red}>+</span>}
                <FormattedNumber
                  value={overdue}
                  options={FORMAT.compact}
                  style={NUMBER_STYLE.red}
                />
              </div>
            </div>
          )}

          {penaltyPct !== undefined && (
            <div className={styles.debtField}>
              <div className={styles.debtText} style={{ fontSize: '0.65rem' }}>
                <div>{t('kLimit')}</div>
                <FormattedNumber
                  value={penaltyPct}
                  options={FORMAT.percent}
                  style={NUMBER_STYLE.big}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.amounts}>
          <div className={styles.progressLegend}>
            <LegendItem
              width={`${pdzPercent}%`}
              value={pdz || undefined}
              options={FORMAT.small}
              style={NUMBER_STYLE.small}
            />
            <LegendItem
              width="100%"
              value={fact || undefined}
              options={FORMAT.compact1}
              style={NUMBER_STYLE.small}
            />
            <LegendItem
              width={`${Math.max(freePercent, 15)}%`}
              value={free || undefined}
              options={FORMAT.compact}
              style={NUMBER_STYLE.small}
            />
          </div>

          <ProgressBar
            pdzPercent={0}
            freePercent={freePercent}
            showPdz={false}
          />

          {limit !== undefined && <Limit limit={limit} />}

          <AmountsList
            items={amounts}
            className={styles.amountsLine}
            style={{ paddingTop: 15 }}
          />
        </div>
      </Card>
    </div>
  );
};
